/**
 * Login Google (acceso a Analytics). Sin captcha — la sesión Firebase ya autentica.
 */
import { useEffect, useState } from 'react';
import {
  loginWithGoogle,
  mapFirebaseAuthError,
  mapAnalyticsSecurityError,
  isInAppBrowser,
  prefersRedirectAuth
} from '../../../services/firebase-auth.js';

const GOOGLE_ICON = (
  <svg className="analytics-google-icon" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

/**
 * @param {{
 *   onSuccess: () => Promise<void>,
 *   error?: string,
 *   resumeEmail?: string
 * }} props
 */
export default function LoginPage({ onSuccess, error: externalError, resumeEmail = '' }) {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState(externalError || '');
  const hasSession = !!String(resumeEmail || '').trim();
  const inApp = isInAppBrowser();
  const mobileAuth = prefersRedirectAuth();

  useEffect(() => {
    if (externalError) setError(externalError);
  }, [externalError]);

  async function finish(afterAuth) {
    setBusy(true);
    setError('');
    try {
      if (afterAuth) await afterAuth();
      setStatus('Verificando permisos de Analytics…');
      await onSuccess();
      setStatus('Entrando…');
    } catch (err) {
      const fb = mapFirebaseAuthError(err);
      const sec = mapAnalyticsSecurityError(err);
      const code = err && err.code ? String(err.code) : '';
      if (
        code === 'auth/popup-closed-by-user' ||
        code === 'auth/cancelled-popup-request' ||
        code === 'auth/redirect-cancelled-by-user'
      ) {
        setStatus('');
        setBusy(false);
        return;
      }
      if (code === 'auth/redirect-in-progress') {
        setStatus('Redirigiendo a Google…');
        return;
      }
      setError(code.indexOf('auth/') === 0 ? fb : sec || fb);
      setStatus('');
      setBusy(false);
    }
  }

  async function handleGoogleLogin() {
    if (busy) return;
    await finish(async () => {
      setStatus(mobileAuth ? 'Redirigiendo a Google…' : 'Abriendo Google…');
      await loginWithGoogle();
      setStatus('Validando usuario…');
    });
  }

  async function handleResume() {
    if (busy) return;
    await finish(null);
  }

  return (
    <div className={'analytics-login' + (busy ? ' is-busy' : '')}>
      <div className="analytics-login-card" aria-busy={busy}>
        <p className="analytics-eyebrow">GI Capital</p>
        <h1>Analytics</h1>
        <p className="analytics-login-lead">
          {busy
            ? status || 'Conectando…'
            : hasSession
              ? `Sesión Google detectada (${resumeEmail}).`
              : 'Ingresá con tu cuenta de Google autorizada.'}
        </p>
        {error ? <p className="analytics-error-inline">{error}</p> : null}

        {hasSession ? (
          <button
            type="button"
            className={'analytics-btn' + (busy ? ' is-loading' : '')}
            disabled={busy}
            aria-busy={busy}
            onClick={handleResume}
          >
            {busy ? (
              <>
                <span className="analytics-btn-spinner" aria-hidden="true" />
                {status || 'Conectando…'}
              </>
            ) : (
              <span>Entrar a Analytics</span>
            )}
          </button>
        ) : (
          <button
            type="button"
            className={'analytics-btn analytics-btn-google' + (busy ? ' is-loading' : '')}
            disabled={busy}
            aria-busy={busy}
            onClick={handleGoogleLogin}
          >
            {busy ? (
              <>
                <span className="analytics-btn-spinner" aria-hidden="true" />
                {status || 'Conectando…'}
              </>
            ) : (
              <>
                {GOOGLE_ICON}
                <span>Continuar con Google</span>
              </>
            )}
          </button>
        )}

        {busy ? (
          <p className="analytics-login-progress" role="status">
            {mobileAuth
              ? 'Te vamos a llevar a Google y volvés acá. No cierres la pestaña.'
              : 'Esto puede tardar unos segundos. No cierres la pestaña.'}
          </p>
        ) : null}
        {inApp ? (
          <p className="analytics-login-hint analytics-login-hint-warn">
            Este visor (WhatsApp, Gmail u otro) suele bloquear Analytics. Abrí
            analytics.gicapital.cl en Safari o Chrome.
          </p>
        ) : (
          <p className="analytics-login-hint">Acceso solo para usuarios autorizados de GI Capital.</p>
        )}
      </div>
    </div>
  );
}
